/**
 * The Practice picker's mission list (10 §4): all thirty-six, the original twelve first under "1987", then
 * the expansion by category. One real button a row - number, name, native ground, base threat - so Tab,
 * Enter and the arrow keys work unaided; Page Up / Page Down jump a group at a time, because thirty-six rows
 * is a long walk with the arrows. The row that is chosen is `aria-pressed`; the one-line summary, the
 * category and the tags of whatever row has the focus are the screen's ABOUT panel's to show.
 */

import './practice.css';
import type { ArchetypeId } from '../../sim/types';
import type { ArchetypeInfo, MissionGroup } from './archetypes';
import { archetypeNumber, groupedArchetypes } from './archetypes';
import type { Describe } from './text';
import { button, el } from './dom';

export interface PickerRow {
  info: ArchetypeInfo;
  group: MissionGroup;
}

/** Every row in keyboard order: group by group, mission number within a group. Pure. */
export function pickerRows(): PickerRow[] {
  return groupedArchetypes().flatMap(({ group, missions }) => missions.map((info) => ({ info, group })));
}

/**
 * Where Page Down (`dir` 1) or Page Up (-1) lands from row `index`: the first row of the next group, or of
 * this group and then the one before it. Stays put at either end. Pure.
 */
export function groupJump(rows: readonly PickerRow[], index: number, dir: 1 | -1): number {
  const here = rows[index];
  if (here === undefined) return index;
  const firstOf = (group: MissionGroup): number => rows.findIndex((r) => r.group === group);
  if (dir === 1) {
    const next = rows.findIndex((r, i) => i > index && r.group !== here.group);
    return next < 0 ? index : next;
  }
  const start = firstOf(here.group);
  if (start < index) return start;
  const before = rows[start - 1];
  return before === undefined ? index : firstOf(before.group);
}

export interface MissionList {
  readonly el: HTMLElement;
  /** The row buttons, in keyboard order. */
  readonly buttons: readonly HTMLButtonElement[];
}

export interface MissionListOptions {
  describe: Describe;
  selected: ArchetypeId;
  /** Enter / click: this is the mission to fly. */
  onSelect(info: ArchetypeInfo): void;
  /** Focus / hover: show what this one is, without choosing it. */
  onPreview(info: ArchetypeInfo): void;
}

export function missionList(options: MissionListOptions): MissionList {
  const { describe } = options;
  const root = el('div', 'menu-pick-list menu-scroll menu-grow');
  root.setAttribute('role', 'group');
  root.setAttribute('aria-label', 'MISSION');
  const rows = pickerRows();
  const buttons: HTMLButtonElement[] = [];
  let chosen = options.selected;

  const paint = (): void => {
    buttons.forEach((b, i) => b.setAttribute('aria-pressed', rows[i]?.info.id === chosen ? 'true' : 'false'));
  };

  let group: MissionGroup | null = null;
  rows.forEach((row, index) => {
    if (row.group !== group) {
      group = row.group;
      const count = rows.filter((r) => r.group === row.group).length;
      const head = el('div', 'menu-pick-group', `${describe(`group:${row.group}`).name} - ${count}`);
      head.setAttribute('role', 'heading');
      head.title = describe(`group:${row.group}`).text;
      root.append(head);
    }
    const { info } = row;
    const d = describe(info.id);
    const terrain = describe(info.biome).name;
    const b = button('', () => {
      chosen = info.id;
      paint();
      options.onSelect(info);
    }, 'menu-pick-row');
    b.setAttribute('aria-label', `${archetypeNumber(info.id)} ${d.name}, ${terrain}, THREAT ${info.baseThreat}`);
    b.title = d.text;
    b.append(
      el('span', 'menu-pick-number', archetypeNumber(info.id)),
      el('span', 'menu-pick-name', d.name),
      el('span', 'menu-pick-terrain', terrain),
      el('span', 'menu-pick-threat', `TR ${info.baseThreat}`),
    );
    b.addEventListener('focus', () => options.onPreview(info));
    b.addEventListener('mouseenter', () => options.onPreview(info));
    // Looking is not choosing: once the eye moves off the list, the panel is about the chosen mission again.
    const revert = (): void => options.onPreview(rows.find((r) => r.info.id === chosen)?.info ?? info);
    b.addEventListener('blur', revert);
    b.addEventListener('mouseleave', revert);
    b.addEventListener('keydown', (ev) => {
      if (ev.key !== 'PageDown' && ev.key !== 'PageUp') return;
      ev.preventDefault();
      const to = buttons[groupJump(rows, index, ev.key === 'PageDown' ? 1 : -1)];
      to?.focus();
      to?.scrollIntoView({ block: 'nearest' });
    });
    buttons.push(b);
    root.append(b);
  });
  paint();

  return { el: root, buttons };
}
